import { Calendar, AlertTriangle, Users, Clock } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface CapacityData {
  total_active: number;
  available_today: number;
  on_pto_today: number;
  on_call_today: number;
  on_pto_employees: Array<{ id: string; name: string; title: string }>;
  on_call_employees: Array<{ id: string; name: string; title: string }>;
  upcoming_pto_next_7_days: number;
  as_of: string;
}

interface Conflict {
  employee_id: string;
  employee_name: string;
  employee_title: string;
  pto_id: string;
  conflict_dates: string[];
  reason: string;
}

interface ConflictData {
  conflicts: Conflict[];
  total: number;
  checked_through: string;
}

interface GapDay {
  date: string;
  available: number;
  pto_count: number;
  gap: boolean;
}

interface GapData {
  coverage_gaps: GapDay[];
  total_gap_days: number;
  threshold: number;
  total_active: number;
  checked_through: string;
}

// ─── Calendar Grid ────────────────────────────────────────────

function CalendarGrid({ gaps, conflicts }: { gaps: GapDay[]; conflicts: Conflict[] }) {
  // Build 30-day data map
  const today = new Date();
  const days: Array<{ date: string; label: number; isGap: boolean; hasConflict: boolean; isToday: boolean }> = [];

  for (let i = 0; i < 30; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().slice(0, 10);
    const isGap = gaps.some(g => g.date === dateStr);
    const hasConflict = conflicts.some(c => c.conflict_dates.includes(dateStr));
    days.push({ date: dateStr, label: d.getDate(), isGap, hasConflict, isToday: i === 0 });
  }

  // Group into weeks
  const weeks: typeof days[] = [];
  let week: typeof days = [];
  // Pad to start of week
  const firstDayOfWeek = today.getDay();
  for (let i = 0; i < firstDayOfWeek; i++) week.push({ date: '', label: 0, isGap: false, hasConflict: false, isToday: false });
  for (const day of days) {
    week.push(day);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) weeks.push(week);

  return (
    <div className="glass rounded-xl p-5 space-y-3">
      <h3 className="text-sm font-semibold text-foreground">30-Day Capacity Heat Map</h3>

      <div className="grid grid-cols-7 gap-1 text-xs text-muted-foreground text-center mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1">
          {week.map((day, di) => (
            <div
              key={di}
              title={day.date || undefined}
              className={`h-9 rounded-lg flex items-center justify-center text-xs font-medium transition-colors ${
                !day.date
                  ? 'invisible'
                  : day.isGap && day.hasConflict
                    ? 'bg-red-500/40 text-red-300'
                    : day.isGap
                      ? 'bg-red-500/20 text-red-400'
                      : day.hasConflict
                        ? 'bg-amber-500/20 text-amber-400'
                        : day.isToday
                          ? 'bg-amber-500/30 text-amber-300 ring-1 ring-amber-500/50'
                          : 'bg-green-500/10 text-green-400'
              }`}
            >
              {day.label || ''}
            </div>
          ))}
        </div>
      ))}

      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/20" /> Full coverage</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/20" /> PTO conflict</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/20" /> Coverage gap</span>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function ShiftIntelligencePage() {
  const { data: capacity, loading: capLoading } = useApi<CapacityData>('people/shift-intel/capacity');
  const { data: conflictData, loading: confLoading } = useApi<ConflictData>('people/shift-intel/conflicts');
  const { data: gapData, loading: gapLoading } = useApi<GapData>('people/shift-intel/coverage-gaps');

  const loading = capLoading || confLoading || gapLoading;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">Shift Intelligence</h1>
          <p className="text-sm text-muted-foreground">Team capacity, PTO vs on-call conflicts, and coverage gaps</p>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading shift data…</div>
      ) : (
        <>
          {/* KPI strip */}
          {capacity && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-green-400" />
                  <p className="text-xs text-muted-foreground">Available Today</p>
                </div>
                <p className="text-2xl font-bold text-green-400">{capacity.available_today}</p>
                <p className="text-xs text-muted-foreground">of {capacity.total_active} active</p>
              </div>
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-amber-400" />
                  <p className="text-xs text-muted-foreground">On PTO Today</p>
                </div>
                <p className={`text-2xl font-bold ${capacity.on_pto_today > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                  {capacity.on_pto_today}
                </p>
              </div>
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <p className="text-xs text-muted-foreground">On Call Today</p>
                </div>
                <p className="text-2xl font-bold text-blue-400">{capacity.on_call_today}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <p className="text-xs text-muted-foreground">Coverage Gaps (30d)</p>
                </div>
                <p className={`text-2xl font-bold ${(gapData?.total_gap_days ?? 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {gapData?.total_gap_days ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">days</p>
              </div>
            </div>
          )}

          {/* Calendar */}
          <CalendarGrid
            gaps={gapData?.coverage_gaps ?? []}
            conflicts={conflictData?.conflicts ?? []}
          />

          <div className="grid lg:grid-cols-2 gap-6">
            {/* PTO conflicts */}
            <div className="glass rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-foreground">
                  PTO / On-Call Conflicts ({conflictData?.total ?? 0})
                </h3>
              </div>

              {conflictData?.conflicts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No conflicts detected in the next 30 days.</p>
              ) : (
                <div className="space-y-3">
                  {conflictData?.conflicts.map((c, i) => (
                    <div key={i} className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{c.employee_name}</span>
                        <span className="text-xs text-muted-foreground">{c.employee_title}</span>
                      </div>
                      <p className="text-xs text-amber-400">{c.reason}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.conflict_dates.map(d => (
                          <span key={d} className="px-1.5 py-0.5 bg-amber-500/10 rounded text-xs text-amber-400">{d}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Coverage gap days */}
            <div className="glass rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-semibold text-foreground">
                  Coverage Gap Days ({gapData?.total_gap_days ?? 0})
                </h3>
              </div>

              {gapData?.coverage_gaps.length === 0 ? (
                <p className="text-sm text-muted-foreground">No coverage gaps in the next 30 days. Team is well-staffed.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Days with fewer than {gapData?.threshold} staff available (out of {gapData?.total_active} active):
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {gapData?.coverage_gaps.map(g => (
                      <div key={g.date} className="px-2.5 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-xs">
                        <p className="font-medium text-red-400">{g.date}</p>
                        <p className="text-muted-foreground">{g.available} available</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* On PTO today */}
          {capacity && capacity.on_pto_employees.length > 0 && (
            <div className="glass rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-foreground">On PTO Today</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {capacity.on_pto_employees.map(emp => (
                  <div key={emp.id} className="flex items-center gap-2 p-2 bg-surface-3 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-xs font-medium text-amber-500">
                      {emp.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{emp.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{emp.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
